import React, { useCallback, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import {
  MaterialReactTable,
  useMaterialReactTable
} from 'material-react-table';
import { useDispatch, useSelector } from 'react-redux';
import useViewAs from 'hooks/useViewAs';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import CarrierDisclaimer from 'tailwind-components/CarrierDisclaimer/CarrierDisclaimer';
import OffboardingModal from 'tailwind-components/Offboarding/OffboardingModal';
import { getCompany } from 'helpers/getCompanyVariables';
import { safePostCall } from 'helpers/firebaseSetup';
import { parseZohoOkMessageOutput } from 'helpers/zoho/response-parsers';
import useHiddenMode, { maskValue } from 'hooks/useHiddenMode';
import { showAlert } from 'state/actions/site';
import endpoints from 'constants/endpoints';
import {
  DISCLAIMER_COLORS,
  DISCLAIMER_ICONS
} from 'helpers/factory/carrierFactory';
import AgentsFiltersToolbar from 'tailwind-components/AgentsFiltersToolbar/AgentsFiltersToolbar';
import {
  AGENT_FILTER_ALL,
  DEFAULT_SORT_OPTION,
  filterAndSortAgents
} from 'helpers/agencyManagement/agent-filters';
import OffboardingActionsCell from './OffboardingActionsCell';
import SecondApprovalConfirmModal from './SecondApprovalConfirmModal';

const company = getCompany();

// offboarding rows flatten the agent and agency names into their own fields
const OFFBOARDING_ROW_ACCESSORS = {
  name: row => row.agentName,
  agency: row => row.agencyName
};

const DOWNLINE_DISCLAIMER = {
  text: 'You are viewing every agent with an open offboarding process, from the selected agency and all agencies in its downline.',
  color: DISCLAIMER_COLORS.BLUE,
  icon: DISCLAIMER_ICONS.INFO
};

const headerCellClass = 'flex h-12 min-h-12 items-center';
const headerCellCenterClass =
  'flex h-12 min-h-12 w-full items-center justify-center';
const CENTERED_COLUMNS = new Set(['status', 'offboardingCell']);

function TextCell({ children }) {
  return <span className='text-[13px] text-grey'>{children}</span>;
}

TextCell.propTypes = {
  children: propTypes.node
};

export function countOpenOffboardingAgents(agencies) {
  return Object.values(agencies ?? {}).reduce(
    (total, agency) => total + Object.keys(agency?.agents ?? {}).length,
    0
  );
}

function flattenOpenOffboardings(agencies) {
  return Object.entries(agencies ?? {}).flatMap(([agencyId, agency]) =>
    Object.entries(agency?.agents ?? {}).map(([agentId, record]) => ({
      agentId,
      agencyId,
      agencyName: agency?.name ?? '',
      agentName: record?.name ?? '',
      Email: record?.email ?? '',
      NPN: record?.npn ?? '',
      Position_at_Agency: record?.position ?? '',
      helperText: record?.helperText ?? '',
      canOffboard: record?.canOffboard === true,
      offboardingRecordId: record?.id ?? null,
      showSecondApprovalBtns: record?.showSecondApprovalBtns === true,
      secondApprovalHelperText: record?.secondApprovalHelperText ?? '',
      secondApprovalAgency: record?.secondApprovalAgency ?? null
    }))
  );
}

function OffboardingBadge() {
  return (
    <div className='flex w-fit items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-600'>
      <span className='h-2 w-2 rounded-full bg-purple-500' />
      Offboarding
    </div>
  );
}

function OpenOffboardingsTable({
  agencies = {},
  loading = false,
  onOffboardingSuccess
}) {
  const dispatch = useDispatch();
  const zohoData = useSelector(state => state.user.zohoData);
  const { targetId: viewAsAgent, requesterId: mainZohoId } = useViewAs();
  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);
  const currentUserId = zohoData?.id;
  const { hidden } = useHiddenMode();

  const userAgencyId = zohoData?.Account_Name?.id;
  const [isOpenOffboardModal, setIsOpenOffboardModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);

  const [secondApproval, setSecondApproval] = useState({
    open: false,
    action: null,
    record: null
  });
  const [isSubmittingSecondApproval, setIsSubmittingSecondApproval] =
    useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [positionFilter, setPositionFilter] = useState(AGENT_FILTER_ALL);
  const [sortOption, setSortOption] = useState(DEFAULT_SORT_OPTION);

  const hideReleaseApprovalQuestion =
    company === 'sunshine'
      ? true
      : !userAgencyData?.Upline_Allows_Showing_RAQ_On_Offboarding_Survey &&
        !userAgencyData?.Override_Upline_RAQ_Offboarding_Survey_Setting;

  const handleOpenOffboardModal = useCallback(agentId => {
    setSelectedAgentId(agentId);
    setIsOpenOffboardModal(true);
  }, []);

  const handleCloseOffboardModal = useCallback(() => {
    setIsOpenOffboardModal(false);
    setSelectedAgentId(null);
  }, []);

  const handleRequestSecondApproval = useCallback((action, record) => {
    setSecondApproval({ open: true, action, record });
  }, []);

  const handleCloseSecondApproval = useCallback(() => {
    if (isSubmittingSecondApproval) return;
    setSecondApproval({ open: false, action: null, record: null });
  }, [isSubmittingSecondApproval]);

  const handleConfirmSecondApproval = useCallback(async () => {
    if (!secondApproval.record?.offboardingRecordId) {
      dispatch(
        showAlert({
          message: 'Missing offboarding record ID',
          code: 'error'
        })
      );
      return;
    }
    setIsSubmittingSecondApproval(true);
    try {
      const res = await safePostCall(endpoints.awsProxy.safePost, {
        endpoint: endpoints.zoho.approveOrDenyOffboardingProcessSecondApproval,
        isZohoRequest: true,
        adminId: mainZohoId,
        offboardingRecordId: secondApproval.record.offboardingRecordId,
        secondApprovalStatus:
          secondApproval.action === 'approve' ? 'Approved' : 'Denied'
      });
      const { ok, message } = parseZohoOkMessageOutput(
        res?.data?.details?.output
      );
      dispatch(showAlert({ message, code: ok ? 'success' : 'error' }));
      setSecondApproval({ open: false, action: null, record: null });
      if (ok) onOffboardingSuccess?.();
    } catch (e) {
      dispatch(
        showAlert({
          message: e?.message || 'Failed to update approval',
          code: 'error'
        })
      );
      setSecondApproval({ open: false, action: null, record: null });
    } finally {
      setIsSubmittingSecondApproval(false);
    }
  }, [dispatch, mainZohoId, onOffboardingSuccess, secondApproval]);

  const rows = useMemo(() => flattenOpenOffboardings(agencies), [agencies]);

  const displayRows = useMemo(
    () =>
      filterAndSortAgents({
        agents: rows,
        searchQuery,
        positionFilter,
        sortOption,
        accessors: OFFBOARDING_ROW_ACCESSORS
      }),
    [rows, positionFilter, searchQuery, sortOption]
  );

  const failedAgenciesDisclaimer = useMemo(() => {
    const failedNames = Object.values(agencies ?? {})
      .filter(agency => agency?.ok === false)
      .map(agency => agency?.name)
      .filter(Boolean);
    if (failedNames.length === 0) return null;
    return {
      text: `Offboarding information could not be loaded for: ${failedNames.join(
        ', '
      )}`,
      color: DISCLAIMER_COLORS.YELLOW,
      icon: DISCLAIMER_ICONS.WARNING
    };
  }, [agencies]);

  const columns = useMemo(
    () => [
      {
        header: 'Agent',
        accessorKey: 'agentName',
        size: 260,
        Cell: cellProps => (
          <div className='flex flex-col'>
            <span className='text-sm font-medium text-black'>
              {cellProps.row.original.agentName || '—'}
            </span>
            <span className='text-xs text-grey'>
              {maskValue(cellProps.row.original.Email, hidden)}
            </span>
          </div>
        )
      },
      {
        header: 'NPN',
        accessorKey: 'NPN',
        size: 120,
        Cell: cellProps => <TextCell>{cellProps.row.original.NPN}</TextCell>
      },
      {
        header: 'Position',
        accessorKey: 'Position_at_Agency',
        size: 120,
        Cell: cellProps => (
          <TextCell>
            {cellProps.row.original.Position_at_Agency || 'Agent'}
          </TextCell>
        )
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 140,
        Cell: () => (
          <div className='flex justify-center'>
            <OffboardingBadge />
          </div>
        )
      },
      {
        header: 'Agency',
        accessorKey: 'agencyName',
        size: 240,
        Cell: cellProps => (
          <TextCell>{cellProps.row.original.agencyName || '—'}</TextCell>
        )
      },
      {
        header: 'Actions',
        accessorKey: 'offboardingCell',
        size: 210,
        Cell: cellProps => (
          <OffboardingActionsCell
            record={cellProps.row.original}
            currentUserId={currentUserId}
            mainZohoId={mainZohoId}
            viewAsAgent={viewAsAgent}
            userAgencyId={userAgencyId}
            onOpenOffboard={handleOpenOffboardModal}
            onRequestSecondApproval={handleRequestSecondApproval}
          />
        )
      }
    ],
    [
      currentUserId,
      mainZohoId,
      viewAsAgent,
      handleOpenOffboardModal,
      handleRequestSecondApproval,
      userAgencyId,
      hidden
    ]
  );

  const table = useMaterialReactTable({
    columns,
    data: displayRows,
    getRowId: row => `${row.agencyId}-${row.agentId}`,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        height: 'fit-content',
        width: '100%',
        boxShadow: 'none'
      }
    },
    muiTableHeadCellProps: headerProps => {
      const accessor = headerProps.column.columnDef.accessorKey;
      if (CENTERED_COLUMNS.has(accessor)) {
        return {
          className: headerCellCenterClass,
          align: 'center',
          sx: {
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'center'
            }
          }
        };
      }
      return { className: headerCellClass };
    },
    muiTableBodyCellProps: cellPropsDef => {
      if (CENTERED_COLUMNS.has(cellPropsDef.column.columnDef.accessorKey)) {
        return { align: 'center' };
      }
      return {};
    },
    renderEmptyRowsFallback: () => (
      <p className='m-0 py-10 text-center text-sm font-normal text-grey'>
        No agents with an open offboarding process
      </p>
    )
  });

  return (
    <div className='flex w-full min-w-0 flex-col gap-3'>
      <CarrierDisclaimer disclaimer={DOWNLINE_DISCLAIMER} />
      {!loading && failedAgenciesDisclaimer && (
        <CarrierDisclaimer disclaimer={failedAgenciesDisclaimer} />
      )}
      {loading ? (
        <div className='flex flex-col items-center justify-center py-12'>
          <LoadingContent message='Loading offboarding data...' />
        </div>
      ) : (
        <>
          <AgentsFiltersToolbar
            agents={rows}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            positionFilter={positionFilter}
            onPositionFilterChange={setPositionFilter}
            sortOption={sortOption}
            onSortChange={setSortOption}
          />
          <MaterialReactTable table={table} />
        </>
      )}
      {isOpenOffboardModal && (
        <OffboardingModal
          source='agencyTable'
          selectedAgentId={selectedAgentId}
          onClose={handleCloseOffboardModal}
          hideReleaseApprovalQuestion={hideReleaseApprovalQuestion}
          onSuccess={onOffboardingSuccess}
        />
      )}
      <SecondApprovalConfirmModal
        record={secondApproval.record}
        action={secondApproval.action}
        open={secondApproval.open}
        onClose={handleCloseSecondApproval}
        onConfirm={handleConfirmSecondApproval}
        isSubmitting={isSubmittingSecondApproval}
      />
    </div>
  );
}

OpenOffboardingsTable.propTypes = {
  agencies: propTypes.object,
  loading: propTypes.bool,
  onOffboardingSuccess: propTypes.func
};

export default OpenOffboardingsTable;
